<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import moment from 'moment'
	import { computed, onMounted, reactive, ref } from 'vue'
	import useRoom from '@/modules/hotels/composables/useRooms'

	import useDateFormat from '@/composables/useDateFormat'
	import { inject } from 'vue'
	import { Collapse } from 'vue-collapsed'
	import { useToast } from 'vue-toastification'

	import ModalComponent from '@/components/ModalComponent.vue'
	import { serviceDescriptor } from '@/config/services'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledTextarea from '@/components/StyledTextarea.vue'
	import LoadingSpin from '@/components/icons/LoadingSpin.vue'

	import useAuthUser from '@/composables/useAuthUser'

	import useUser from '@/modules/users/composables/useUser'

	import { isAxiosError } from 'axios'
	import useRequestCancel from '../composables/useRequestCancel'
	import useRequestDownlandPdf from '../composables/useRequestDownlandPdf'
	import useRequestInvoice, { downloadBlobAs, downloadInvoiceXml } from '../composables/useRequestInvoice'
	import RequestInvoiceBar from '@/modules/invoices/components/RequestInvoiceBar.vue'
	import useRequestResendNotifications from '../composables/useRequestResendNotifications'
	import { getInvoiceStatusDisplay } from '@/modules/invoices/interfaces/invoice'
	import { Request } from '../interfaces/cef'
	import { tripReasonLabel } from '@/enums/tripReasons'
	import { isQuotingFare } from '../utils/tripFormatters'
	import { formatProviderDeadline } from '@/modules/hotels/utils/hotelDates'

	interface Props {
		request: Request
	}
	interface Emits {
		(e: 'refetch'): void
	}

	const props = defineProps<Props>()

	// Tipo de habitación con el mismo texto que el detalle del hotel donde se reservó
	// (useRoom traduce el código de Amadeus). Si el proveedor mandó el nombre, ése
	// gana; `roomLabel` del back queda de respaldo.
	const roomTypeLabel = computed(
		() =>
			props.request.roomName?.trim() ||
			useRoom(props.request.typeCode).formatted ||
			props.request.roomLabel ||
			null
	)
	const tripReason = computed(() => tripReasonLabel(props.request.reason))
	const emits = defineEmits<Emits>()

	const { searchParams } = inject('cef') as { searchParams: () => void }
	const openRates = ref(true)

	const isQuoting = computed(() =>
		isQuotingFare(props.request.stateId, props.request.clientFare)
	)
	const queryClient = useQueryClient()
	const toast = useToast()
	const notifications = reactive([
		{
			label: 'Socio',
			value: 'member',
			send: false,
		},
		{
			label: 'Viajero',
			value: 'traveler',
			send: false,
		},
		{
			label: 'Supervisor',
			value: 'supervisor',
			send: false,
		},
	])

	// Manejo de tooltips para móvil
	onMounted(() => {
		// Agregar event listeners para dispositivos táctiles
		const tooltipTriggers = document.querySelectorAll('.group')

		tooltipTriggers.forEach((trigger) => {
			const button = trigger.querySelector('button')
			const tooltip = trigger.querySelector('.absolute.bottom-full')

			if (button && tooltip) {
				let tooltipTimeout: NodeJS.Timeout

				// Mostrar tooltip en touch/click en móvil
				button.addEventListener('touchstart', (e) => {
					e.preventDefault()
					tooltip.classList.remove('opacity-0')
					tooltip.classList.add('opacity-100')

					// Auto-ocultar después de 2 segundos
					clearTimeout(tooltipTimeout)
					tooltipTimeout = setTimeout(() => {
						tooltip.classList.add('opacity-0')
						tooltip.classList.remove('opacity-100')
					}, 2000)
				})

				// Ocultar tooltip cuando se toque fuera
				document.addEventListener('touchstart', (e) => {
					if (!trigger.contains(e.target as Node)) {
						tooltip.classList.add('opacity-0')
						tooltip.classList.remove('opacity-100')
						clearTimeout(tooltipTimeout)
					}
				})
			}
		})
	})

	const { requestCancelMutationAsync, requestCancelMutationLoading } =
		useRequestCancel({
			id: 0,
		})

	const { requestId, refetch, isFetching, isRefetching } =
		useRequestDownlandPdf({
			immediate: false,
			requestId: props.request.id.toString(),
		})

	const {
		resendNotificationAsync,
		requestId: requestValue,
		resendNotificationsLoading,
	} = useRequestResendNotifications({
		requestId: props.request.id,
	})

	const toggleRates = () => {
		openRates.value = !openRates.value
	}

	const formatRates = (rate: number) => {
		const formatter = new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
		})

		return formatter.format(rate)
	}

	const diffDays = (start: string, end: string): number => {
		const startDate = moment(start)
		const endDate = moment(end)

		return endDate.diff(startDate, 'days')
	}

	const dailyRate = (rate: number, days: number) => {
		return formatRates(rate / days)
	}

	const selectRequest = ref<number | null>(null)

	const modalCancelRequest = ref(false)

	const actionCancelRequest = () => {
		modalCancelRequest.value = !modalCancelRequest.value
	}

	const comment = ref('')

	const cancelRequest = async () => {
		if (!comment.value)
			return toast.error('Por favor, escribe un comentario de cancelación')

		await requestCancelMutationAsync({
			id: selectRequest.value!,
			comment: comment.value,
		})

		await emits('refetch')
		await searchParams()
		await queryClient.refetchQueries(['cefsRequests', selectRequest.value])
		comment.value = ''

		actionCancelRequest()
		selectRequest.value = null
	}

	const validateDeadline = (deadline: string | null) => {
		return (
			deadline &&
			new Date() <= new Date(deadline) &&
			props.request.stateName !== 'Proximo'
		)
	}

	/**
	 * Hotelbeds (HBX 3.8): la cancelación sin costo acaba con el primer cargo, a la hora del
	 * hotel; `deadline` sólo guarda el día. Después cancela un asesor (#954).
	 */
	const hotelbedsFreeUntil = computed(() => {
		const froms = (props.request.cancellationPolicies ?? []).map((p) => p.from)
		return froms.sort((a, b) => Date.parse(a) - Date.parse(b))[0] ?? null
	})

	const hotelbedsDeadlinePassed = computed(
		() =>
			hotelbedsFreeUntil.value !== null &&
			Date.parse(hotelbedsFreeUntil.value) <= Date.now()
	)

	const canCancelReservation = computed(() => {
		if (props.request.stateName === 'Cancelado') {
			return false
		}

		if (hotelbedsDeadlinePassed.value) {
			return false
		}

		const progressStage = props.request.cefProgressStage

		if (progressStage) {
			if (progressStage === 'past') {
				return false
			}

			if (progressStage === 'current') {
				return false
			}

			if (progressStage === 'future') {
				if (hotelbedsFreeUntil.value) {
					return true
				}
				if (!props.request.deadline) {
					return false
				}
				return new Date(props.request.deadline) >= new Date()
			}
		} else {
			const now = new Date()
			const startDate = new Date(props.request.startDate)
			const endDate = new Date(props.request.endDate)

			if (now > endDate) {
				return false
			}

			if (now >= startDate && now <= endDate) {
				return false
			}

			if (now < startDate) {
				if (hotelbedsFreeUntil.value) {
					return true
				}
				return props.request.deadline ?
						new Date(props.request.deadline) >= now
					:	false
			}
		}

		return false
	})

	const actionReSendEmail = ref(false)

	const handleResendNotification = async () => {
		if (notifications.every((notification) => !notification.send)) {
			toast.error('Selecciona al menos un correo para reenviar la notificación')
			return
		}

		const recipients = notifications
			.filter((notification) => notification.send)
			.map((notification) => notification.value)

		// ⚠️ Con `await` y dentro del try: antes se disparaba la petición y el toast de
		// éxito salía en la misma línea, sin esperar respuesta. Decía «enviadas» aunque el
		// backend contestara 409 —el plan de la empresa no incluye el módulo—, 403 o nada.
		// El error ya lo avisa el composable; aquí lo único que hay que hacer es no
		// celebrar ni limpiar el formulario.
		try {
			await resendNotificationAsync({
				recipients,
			})
		} catch {
			return
		}

		toast.success(
			`Notificaciones enviadas a ${notifications.filter((n) => n.send).length} destinatarios`
		)
		actionReSendEmail.value = false

		notifications.forEach((notification) => {
			notification.send = false
		})
	}

	const statusMap: Record<string, { background: string; message: string }> = {
		confirmado: {
			background: 'bg-green-100 text-green-800 border border-green-200',
			message: 'Confirmado',
		},
		creado: {
			background: 'bg-amber-100 text-amber-800 border border-amber-200',
			message: 'Pendiente',
		},
		cancelado: {
			background: 'bg-red-100 text-red-800 border border-red-200',
			message: 'Cancelado',
		},
		'pendiente pago': {
			background: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
			message: 'Pendiente Pago',
		},
		facturado: {
			background: 'bg-green-100 text-green-800 border border-green-200',
			message: 'Facturado',
		},
		pagado: {
			background: 'bg-green-100 text-green-800 border border-green-200',
			message: 'Pagado',
		},
		procesada: {
			background: 'bg-green-100 text-green-800 border border-green-200',
			message: 'Confirmado',
		},
	}

	const getStatus = (state: string) =>
		computed(() => {
			const normalizedState = (state || '').toLowerCase().trim()
			return statusMap[normalizedState] || {
				background: 'bg-gray-100 text-gray-800 border border-gray-200',
				message: state || 'En proceso',
			}
		})

	const { user: member } = useUser({
		params: {
			id: props.request.memberId,
		},
		immediate: true,
	})

	// PROVISIONAL: `request.memberName`/`request.traveler.costCenterName` no
	// vienen pobladas de forma confiable en `/api/cefs/:id/requests` (por eso
	// el `member` de arriba ya se resuelve con su propio fetch en vez de
	// confiar en el campo plano). Se usa `data` en lugar de `user` para no
	// pisar el ref global compartido que ya usan `member`/`supervisor`.
	// vue-query dedupea por queryKey (mismo memberId/travelerId en todo el
	// itinerario => una sola petición en vuelo), así que esto no multiplica
	// llamadas por tarjeta. Cuando BookingV2 pueble esos campos en la
	// respuesta, quitar estos dos useUser y volver a leer los campos planos.
	const { data: creator, isLoading: isLoadingCreator } = useUser({
		params: {
			id: props.request.memberId,
		},
		immediate: true,
	})

	const { data: travelerDetail, isLoading: isLoadingTravelerDetail } = useUser({
		params: {
			id: props.request.travelerId,
		},
		immediate: true,
	})

	const { user: supervisor } = useUser({
		params: {
			id: props.request.traveler?.supervisorId,
		},
		immediate: Boolean(props.request.traveler?.supervisorId),
	})

	const { user } = useAuthUser()

	const isInvoiceable = computed(() => {
		if (props.request.stateName === 'Cancelado') return false
		const endDate = new Date(props.request.endDate)
		const oneMonthAgo = new Date()
		oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
		return endDate >= oneMonthAgo
	})

	const { invoice, isLoading: isLoadingInvoice, isActing, createDraftAsync, stampAsync, previewAsync } = useRequestInvoice(
		props.request.id,
		isInvoiceable,
	)

	const showInvoicePreviewModal = ref(false)
	const previewPdfUrl = ref<string | null>(null)
	const previewPdfEmbedUrl = computed(() => previewPdfUrl.value ? `${previewPdfUrl.value}#toolbar=0&navpanes=0` : null)
	const draftInvoice = ref<import('@/modules/invoices/interfaces/invoice').Invoice | null>(null)
	const stampError = ref<string | null>(null)
	const stampedInvoice = ref<import('@/modules/invoices/interfaces/invoice').Invoice | null>(null)
	const isDownloadingXml = ref(false)

	const closeInvoiceModal = () => {
		showInvoicePreviewModal.value = false
		draftInvoice.value = null
		stampError.value = null
		stampedInvoice.value = null
		isDownloadingXml.value = false
		if (previewPdfUrl.value) {
			URL.revokeObjectURL(previewPdfUrl.value)
			previewPdfUrl.value = null
		}
	}

	const handleDownloadPdf = () => {
		if (!previewPdfUrl.value || !stampedInvoice.value) return
		downloadBlobAs(previewPdfUrl.value, `cfdi-${stampedInvoice.value.uuid ?? stampedInvoice.value.id}.pdf`)
	}

	const handleDownloadXml = async () => {
		const companyId = props.request.traveler?.companyId
		if (!stampedInvoice.value) return
		if (!companyId) {
			toast.error('No se encontró la empresa del viajero para descargar el XML')
			return
		}
		isDownloadingXml.value = true
		try {
			await downloadInvoiceXml(companyId, stampedInvoice.value.id, stampedInvoice.value.uuid ?? String(stampedInvoice.value.id))
		} catch {
			toast.error('No se pudo descargar el XML')
		} finally {
			isDownloadingXml.value = false
		}
	}

	const handleCreateDraft = async () => {
		if (!isInvoiceable.value) return
		const created = await createDraftAsync()
		draftInvoice.value = created
		stampError.value = null
		stampedInvoice.value = null
		showInvoicePreviewModal.value = true
		previewPdfUrl.value = await previewAsync(created.id)
	}

	const handleOpenExistingDraft = async () => {
		if (!invoice.value) return
		draftInvoice.value = invoice.value
		stampError.value = null
		stampedInvoice.value = null
		showInvoicePreviewModal.value = true
		previewPdfUrl.value = await previewAsync(invoice.value.id)
	}

	const handleViewStampedInvoice = async () => {
		if (!invoice.value) return
		stampedInvoice.value = invoice.value
		draftInvoice.value = null
		stampError.value = null
		showInvoicePreviewModal.value = true
		previewPdfUrl.value = await previewAsync(invoice.value.id).catch(() => null)
	}

	const handleStamp = async () => {
		const invoiceId = draftInvoice.value?.id
		if (!invoiceId) return
		if (draftInvoice.value?.status === 'cancelada') {
			stampError.value = 'Esta factura está cancelada y no puede timbrarse.'
			return
		}
		stampError.value = null
		try {
			const result = await stampAsync(invoiceId)
			stampedInvoice.value = result ?? null
			if (previewPdfUrl.value) {
				URL.revokeObjectURL(previewPdfUrl.value)
				previewPdfUrl.value = null
			}
			if (stampedInvoice.value) {
				previewPdfUrl.value = await previewAsync(stampedInvoice.value.id).catch(() => null)
			}
		} catch (err: unknown) {
			const msg = isAxiosError(err)
				? (err.response?.data as { message?: string })?.message
				: err instanceof Error ? err.message : null
			stampError.value = msg ?? 'No se pudo timbrar la factura. Verifica que los datos fiscales del receptor estén completos.'
		}
	}


	const invoiceStatusVariant: Record<string, string> = {
		green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
		amber: 'bg-amber-50 text-amber-700 border-amber-200',
		red: 'bg-red-50 text-red-700 border-red-200',
		gray: 'bg-gray-100 text-gray-500 border-gray-200',
	}

	// El badge dice QUÉ pasó ("Cancelado"), no POR QUÉ. El motivo lo escribe
	// quien cancela y el backend lo guarda junto con la fecha; mientras
	// RequestResource no los serialice llegan `undefined` y se muestra el estado
	// "sin motivo registrado" en vez de inventar uno.
	const isCancelled = computed(() => props.request.stateName === 'Cancelado')

	const cancelReason = computed(() => props.request.cancelReason?.trim() || '')

	const cancelledOn = computed(() =>
		props.request.cancelledAt ?
			useDateFormat(props.request.cancelledAt).date
		:	''
	)

	const confirmation = computed(() => {
		return props.request.confirmation ?
				`No. Confirmación: #${props.request.confirmation}`
			:	'Sin confirmación'
	})

	const isPlane = computed(
		() => props.request.serviceName?.toLowerCase() === 'plane'
	)

	const isHotel = computed(
		() => props.request.serviceName?.toLowerCase() === 'hotel'
	)

	// Del registry, que resuelve en cualquier casing.
	const serviceIcon = computed(
		() => serviceDescriptor(props.request.serviceName).icon
	)

	// Flight route: use outbound_destination for round trips
	const flightRoute = computed(() => {
		if (!isPlane.value) return null
		const origin = props.request.origin
		const destination = props.request.destination
		const outbound = props.request.flightDetails?.outboundDestination ?? null

		if (origin && destination && origin === destination && outbound) {
			return { from: origin, to: outbound, isRoundTrip: true }
		}
		if (origin && destination && origin !== destination) {
			return { from: origin, to: destination, isRoundTrip: false }
		}
		if (origin) {
			return { from: origin, to: null, isRoundTrip: false }
		}
		return null
	})

	// City names from airports
	const originCity = computed(
		() => props.request.originAirport?.cityName ?? null
	)
	const destinationCity = computed(
		() => props.request.destinationAirport?.cityName ?? null
	)
</script>

<template>
	<div class="space-y-3">
		<!-- Header con información principal -->
		<div class="flex justify-between items-start">
			<div class="flex-1 min-w-0 space-y-2">
				<div class="flex items-start gap-2 min-w-0">
					<div class="p-1 bg-blue-100 rounded-lg shrink-0">
						<span class="material-symbols-outlined text-blue-600 text-sm">
							{{ serviceIcon }}
						</span>
					</div>
					<h3 class="font-semibold text-gray-900 text-base capitalize min-w-0">
						{{ request.suppliableName }}
					</h3>
				</div>

				<!-- Flight route -->
				<div
					v-if="isPlane && flightRoute"
					class="flex items-center gap-2 pl-7 mt-0.5 flex-wrap"
				>
					<span class="text-sm font-bold text-gray-800">
						{{ flightRoute.from }}
					</span>
					<template v-if="flightRoute.to">
						<span class="material-symbols-outlined text-gray-400 text-xs">
							{{ flightRoute.isRoundTrip ? 'sync_alt' : 'arrow_forward' }}
						</span>
						<span class="text-sm font-bold text-gray-800">
							{{ flightRoute.to }}
						</span>
					</template>
					<span
						v-if="flightRoute.isRoundTrip"
						class="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded"
					>
						Ida y vuelta
					</span>
					<span
						v-if="originCity || destinationCity"
						class="text-[10px] text-gray-400"
					>
						{{ originCity }}
						<template v-if="originCity && destinationCity"> → </template>
						{{ destinationCity }}
					</span>
					<span
						v-if="request.pnr"
						class="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded"
					>
						PNR: {{ request.pnr }}
					</span>
				</div>

				<div class="flex items-center gap-2 pl-7">
					<span class="text-sm text-gray-600 font-medium">{{
						confirmation
					}}</span>
				</div>

				<!-- Tipo de habitación: el mismo texto que la confirmación al huésped. -->
				<div
					v-if="isHotel && roomTypeLabel"
					class="flex items-center gap-1.5 pl-7 text-sm text-gray-600"
				>
					<span
						class="material-symbols-outlined text-[16px] text-gray-400"
						aria-hidden="true"
						>bed</span
					>
					<span class="font-medium">{{ roomTypeLabel }}</span>
				</div>
			</div>

			<!-- Botones de Email y Descarga (solo para usuarios internos) -->
			<div class="flex gap-2">
				<!-- Botón de Email -->
				<div
					class="relative group"
					v-if="user?.internal && request.stateName !== 'Cancelado'"
				>
					<button
						@click="
							() => {
								actionReSendEmail = true
								requestValue = request.id
							}
						"
						class="group relative inline-flex items-center gap-2 px-3 py-2 text-emerald-700 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg hover:from-emerald-100 hover:to-green-100 hover:border-emerald-300 focus:ring-2 focus:ring-emerald-200 active:scale-[0.98] transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed text-xs font-medium"
						:disabled="resendNotificationsLoading"
					>
						<div
							class="absolute inset-0 rounded-lg bg-emerald-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
						></div>
						<LoadingSpin
							v-if="resendNotificationsLoading"
							class="w-3 h-3 relative z-10"
							dark
						/>
						<svg
							v-else
							class="w-3 h-3 relative z-10 group-hover:scale-110 transition-transform duration-200"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
							></path>
						</svg>
						<span class="relative z-10">
							{{
								resendNotificationsLoading ? 'Enviando...' : 'Reenviar Email'
							}}
						</span>
					</button>
				</div>

				<!-- Botón de Descarga -->
				<div class="relative group">
					<button
						@click="
							() => {
								requestId = request.id.toString()
								refetch()
							}
						"
						class="group relative inline-flex items-center gap-2 px-3 py-2 text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-200 active:scale-[0.98] transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-sm text-xs font-medium"
						:disabled="isFetching"
					>
						<div
							class="absolute inset-0 rounded-lg bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
						></div>
						<LoadingSpin
							v-if="isFetching"
							class="w-3 h-3 relative z-10"
						/>
						<svg
							v-else
							class="w-3 h-3 relative z-10 group-hover:scale-110 transition-transform duration-200"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 712-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							></path>
						</svg>
						<span class="relative z-10">
							{{ isFetching ? 'Descargando...' : 'Descargar PDF' }}
						</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Información de fechas y estado -->
		<div
			class="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200"
		>
			<div class="flex items-center gap-2">
				<svg
					class="w-4 h-4 text-gray-500"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
					></path>
				</svg>
				<span class="text-sm text-gray-600 font-medium capitalize">
					{{ request.formattedDates }}
				</span>
			</div>
			<span
				class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
				:class="getStatus(request.stateName).value.background"
			>
				{{ getStatus(request.stateName).value.message }}
			</span>
		</div>

		<!-- Motivo de la cancelación -->
		<div
			v-if="isCancelled"
			class="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
		>
			<span
				class="material-symbols-outlined flex-shrink-0 text-[20px] text-red-500"
				>cancel</span
			>
			<div class="min-w-0 text-sm">
				<p class="font-medium text-red-800">
					Reserva cancelada<template v-if="cancelledOn">
						el {{ cancelledOn }}</template
					>
				</p>
				<p
					v-if="cancelReason"
					class="mt-0.5 break-words text-red-700"
				>
					{{ cancelReason }}
				</p>
				<p
					v-else
					class="mt-0.5 text-red-600/80"
				>
					No se registró un motivo.
				</p>
			</div>
		</div>

		<!-- Centro de costos y responsable -->
		<div
			v-if="
				isLoadingTravelerDetail ||
				isLoadingCreator ||
				travelerDetail?.costCenterName ||
				tripReason ||
				creator?.fullName
			"
			class="flex flex-col gap-2"
		>
			<div
				v-if="isLoadingTravelerDetail"
				class="flex items-center gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200"
			>
				<div class="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
			</div>
			<div
				v-else-if="travelerDetail?.costCenterName"
				class="flex items-center gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200"
			>
				<span class="material-symbols-outlined text-gray-400 text-sm"
					>account_balance</span
				>
				<span class="text-xs text-gray-600">
					<span class="text-gray-400">Centro de costos:</span>
					{{ travelerDetail.costCenterName }}
				</span>
			</div>

			<div
				v-if="tripReason"
				class="flex items-center gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200"
			>
				<span class="material-symbols-outlined text-gray-400 text-sm"
					>luggage</span
				>
				<span class="text-xs text-gray-600 break-words min-w-0">
					<span class="text-gray-400">Motivo del viaje:</span>
					{{ tripReason }}
				</span>
			</div>

			<div
				v-if="isLoadingCreator"
				class="flex items-center gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200"
			>
				<div class="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
			</div>
			<div
				v-else-if="creator?.fullName"
				class="flex items-center gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200"
			>
				<span class="material-symbols-outlined text-gray-400 text-sm"
					>badge</span
				>
				<span class="text-xs text-gray-600 flex items-center gap-1 flex-wrap">
					<span class="text-gray-400">Creado por:</span>
					{{ creator.fullName }}
					<img
						v-if="creator.internal"
						src="@/assets/imgs/icono_enlace.png"
						class="!w-3.5"
						alt="Enlace"
					/>
				</span>
			</div>
		</div>

		<!-- Sección de tarifas -->
		<div class="border border-gray-200 rounded-lg overflow-hidden">
			<!-- Flight: total fare (no daily breakdown) -->
			<template v-if="isPlane">
				<div class="p-3 bg-white">
					<div class="flex justify-between items-center">
						<span class="text-sm text-gray-600 font-medium">
							Tarifa total del vuelo
						</span>
						<p
							class="font-bold"
							:class="isQuoting ? 'text-sm text-amber-600' : 'text-lg text-gray-900'"
						>
							<template v-if="isQuoting">Cotizando…</template>
							<template v-else>
								{{ formatRates(Number(request.clientFare)) }}
								{{ request.currency }}
							</template>
						</p>
					</div>
				</div>
			</template>

			<!-- Hotel/Other: expandable daily breakdown -->
			<template v-else>
				<div
					class="w-full flex justify-between items-center p-3 bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-200"
					@click="toggleRates"
				>
					<div class="flex gap-2 items-center">
						<span class="text-sm text-gray-600 font-medium">
							{{ diffDays(request.startDate, request.endDate) }}
							{{
								diffDays(request.startDate, request.endDate) > 1 ? 'noches' : (
									'noche'
								)
							}}
						</span>
					</div>

					<div class="text-right">
						<p
							class="font-bold"
							:class="isQuoting ? 'text-sm text-amber-600' : 'text-lg text-gray-900'"
						>
							<template v-if="isQuoting">Cotizando…</template>
							<template v-else>
								{{ formatRates(Number(request.clientFare)) }}
								{{ request.currency }}
							</template>
						</p>
					</div>
				</div>
			</template>
		</div>

		<!-- Información de cancelación -->
		<div
			:class="[
				'rounded-lg p-3 border',
				canCancelReservation ?
					'bg-red-50 border-red-200'
				:	'bg-gray-50 border-gray-200',
			]"
		>
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-2 mb-1">
					<svg
						:class="[
							'w-4 h-4',
							canCancelReservation ? 'text-red-600' : 'text-gray-500',
						]"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
						></path>
					</svg>
					<span
						:class="[
							'text-xs font-medium',
							canCancelReservation ? 'text-red-700' : 'text-gray-700',
						]"
						>Política de Cancelación</span
					>
				</div>

				<!-- Botón de cancelación -->
				<div
					class="relative group"
					v-if="canCancelReservation"
				>
					<button
						@click="
							() => {
								actionCancelRequest()
								selectRequest = request.id
							}
						"
						class="group relative inline-flex items-center gap-2 px-3 py-2 text-red-700 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-lg hover:from-red-100 hover:to-rose-100 hover:border-red-300 focus:ring-2 focus:ring-red-200 active:scale-[0.98] transition-all duration-200 shadow-sm hover:shadow-md text-xs font-medium"
					>
						<div
							class="absolute inset-0 rounded-lg bg-red-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
						></div>
						<svg
							class="w-3 h-3 relative z-10 group-hover:scale-110 transition-transform duration-200"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							></path>
						</svg>
						<span class="relative z-10">Cancelar Reserva</span>
					</button>
				</div>
			</div>

			<div class="mt-2">
				<!-- Si se puede cancelar, mostrar fecha límite -->
				<p
					v-if="canCancelReservation && hotelbedsFreeUntil"
					class="text-sm font-semibold text-red-700"
				>
					Cancelación sin costo hasta:
					{{ formatProviderDeadline(hotelbedsFreeUntil) }}
				</p>
				<p
					v-else-if="canCancelReservation && request.deadline"
					class="text-sm font-semibold text-red-700"
				>
					Fecha límite: {{ moment(request.deadline).format('DD/MM/YYYY') }}
				</p>

				<!-- Mensajes cuando NO se puede cancelar -->
				<p
					v-else-if="request.stateName === 'Cancelado'"
					class="text-sm font-medium text-gray-600"
				>
					Esta reservación ya ha sido cancelada
				</p>
				<p
					v-else-if="
						request.cefProgressStage === 'past' ||
						new Date() > new Date(request.endDate)
					"
					class="text-sm font-medium text-gray-600"
				>
					No se puede cancelar - El viaje ya finalizó
				</p>
				<p
					v-else-if="
						request.cefProgressStage === 'current' ||
						(new Date() >= new Date(request.startDate) &&
							new Date() <= new Date(request.endDate))
					"
					class="text-sm font-medium text-gray-600"
				>
					No se puede cancelar - El viaje está en curso
				</p>
				<!-- Hotelbeds (#954): después del plazo sin costo cancela un asesor. -->
				<p
					v-else-if="hotelbedsDeadlinePassed && hotelbedsFreeUntil"
					class="text-sm font-medium text-gray-600"
				>
					El plazo para cancelar sin costo venció el
					{{ formatProviderDeadline(hotelbedsFreeUntil) }}. Para cancelar,
					comunícate con un asesor.
				</p>
				<p
					v-else-if="
						request.deadline && new Date() > new Date(request.deadline)
					"
					class="text-sm font-medium text-gray-600"
				>
					No se puede cancelar - La fecha límite de cancelación ya pasó ({{
						moment(request.deadline).format('DD/MM/YYYY')
					}})
				</p>
				<p
					v-else
					class="text-sm font-medium text-gray-600"
				>
					No se puede cancelar la reservación
				</p>
			</div>
		</div>
	</div>

	<!-- Factura -->
	<RequestInvoiceBar
		:invoice="invoice ?? null"
		:enabled="isInvoiceable"
		:can-create="request.paymentMethod !== 'credit'"
		:is-acting="isActing"
		@create="handleCreateDraft"
		@view-draft="handleOpenExistingDraft"
		@view-stamped="handleViewStampedInvoice"
	/>

	<!-- Modal previsualización de borrador -->
	<ModalComponent
		:model-value="showInvoicePreviewModal"
		class="max-w-2xl w-full"
		@update:model-value="closeInvoiceModal"
	>
		<template #modalBody>
			<div class="bg-white rounded-xl overflow-hidden">
				<!-- Header -->
				<div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
					<div class="flex items-center gap-2">
						<span class="material-symbols-outlined text-[18px] text-gray-400">receipt_long</span>
						<h3 class="text-base font-semibold text-gray-900">
							{{ stampedInvoice ? 'Factura timbrada' : 'Borrador de factura' }}
						</h3>
						<span
							v-if="!stampedInvoice"
							class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200"
						>
							Borrador
						</span>
						<span
							v-else
							class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"
						>
							Timbrada
						</span>
					</div>
					<button
						type="button"
						class="text-gray-400 hover:text-gray-600 transition-colors"
						@click="closeInvoiceModal"
					>
						<span class="material-symbols-outlined text-[20px]">close</span>
					</button>
				</div>

				<div class="p-6 flex flex-col gap-4">

					<!-- Estado: timbrado exitoso -->
					<div v-if="stampedInvoice" class="flex flex-col gap-4">
						<div class="flex flex-col items-center gap-1 py-3">
							<span class="material-symbols-outlined text-[40px] text-emerald-500">check_circle</span>
							<p class="text-base font-semibold text-gray-900">¡Factura timbrada correctamente!</p>
							<p v-if="stampedInvoice.issuerName" class="text-xs text-gray-400">{{ stampedInvoice.issuerName }} · {{ stampedInvoice.issuerRfc }}</p>
						</div>
						<div class="grid gap-3" :class="stampedInvoice.issuerName ? 'grid-cols-3' : 'grid-cols-2'">
							<div v-if="stampedInvoice.issuerName" class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Emisor</p>
								<p class="text-sm font-medium text-gray-900">{{ stampedInvoice.issuerName }}</p>
								<p class="text-xs text-gray-500 font-mono mt-0.5">{{ stampedInvoice.issuerRfc ?? '—' }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Receptor</p>
								<p class="text-sm font-medium text-gray-900">{{ stampedInvoice.receiverName ?? stampedInvoice.legalNameReceptor ?? '—' }}</p>
								<p class="text-xs text-gray-500 font-mono mt-0.5">{{ stampedInvoice.receiverRfc ?? stampedInvoice.rfcReceptor ?? '—' }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Total</p>
								<p class="text-sm font-bold text-[#021F58]">{{ stampedInvoice.currency }} ${{ stampedInvoice.total }}</p>
								<p class="text-[10px] text-gray-400 mt-0.5">Subtotal ${{ stampedInvoice.subtotal }}</p>
							</div>
						</div>
						<div class="grid grid-cols-3 gap-3">
							<div class="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
								<p class="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Folio</p>
								<p class="text-sm font-mono font-medium text-emerald-900">{{ stampedInvoice.folio ?? '—' }}</p>
							</div>
							<div class="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
								<p class="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Fecha timbrado</p>
								<p class="text-xs font-medium text-emerald-900">{{ stampedInvoice.stampedAt ? moment(stampedInvoice.stampedAt).format('DD/MM/YYYY HH:mm') : '—' }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Uso CFDI</p>
								<p class="text-sm font-medium text-gray-900">{{ stampedInvoice.useCfdi ?? '—' }}</p>
							</div>
						</div>
						<div v-if="stampedInvoice.uuid" class="rounded-lg bg-gray-50 border border-gray-100 p-3">
							<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">UUID</p>
							<p class="text-xs font-mono text-gray-700 break-all">{{ stampedInvoice.uuid }}</p>
						</div>
						<!-- PDF del CFDI timbrado -->
						<div class="rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style="height: 320px">
							<iframe
								v-if="previewPdfEmbedUrl"
								:src="previewPdfEmbedUrl"
								class="w-full h-full"
								type="application/pdf"
							/>
							<div v-else class="flex flex-col items-center justify-center h-full gap-2 text-sm text-gray-400">
								<svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
								Cargando CFDI...
							</div>
						</div>
					</div>

					<!-- Estado: borrador -->
					<div v-else-if="draftInvoice" class="flex flex-col gap-3">
						<!-- Emisor / Receptor -->
						<div class="grid gap-3" :class="draftInvoice.issuerName ? 'grid-cols-2' : 'grid-cols-1'">
							<div v-if="draftInvoice.issuerName" class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Emisor</p>
								<p class="text-sm font-medium text-gray-900 leading-tight">{{ draftInvoice.issuerName }}</p>
								<p class="text-xs text-gray-500 font-mono mt-0.5">{{ draftInvoice.issuerRfc ?? '—' }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Receptor</p>
								<p class="text-sm font-medium text-gray-900 leading-tight">{{ draftInvoice.receiverName ?? draftInvoice.legalNameReceptor ?? '—' }}</p>
								<p class="text-xs text-gray-500 font-mono mt-0.5">{{ draftInvoice.receiverRfc ?? draftInvoice.rfcReceptor ?? '—' }}</p>
							</div>
						</div>

						<!-- Montos -->
						<div class="grid grid-cols-3 gap-3">
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Subtotal</p>
								<p class="text-sm font-semibold text-gray-900">{{ draftInvoice.currency }} ${{ draftInvoice.subtotal }}</p>
							</div>
							<div class="rounded-lg bg-[#021F58]/5 border border-[#021F58]/10 p-3">
								<p class="text-[10px] font-semibold text-[#021F58]/60 uppercase tracking-wider mb-1">Total</p>
								<p class="text-sm font-bold text-[#021F58]">{{ draftInvoice.currency }} ${{ draftInvoice.total }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">T. cambio</p>
								<p class="text-sm font-medium text-gray-900">{{ draftInvoice.exchangeRate ?? '1.0000' }}</p>
							</div>
						</div>

						<!-- Datos fiscales -->
						<div class="grid grid-cols-4 gap-3">
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Tipo</p>
								<p class="text-sm font-medium text-gray-900">{{ { I: 'Ingreso', E: 'Egreso', P: 'Pago', N: 'Nómina', T: 'Traslado' }[draftInvoice.type ?? ''] ?? draftInvoice.type ?? '—' }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Uso CFDI</p>
								<p class="text-sm font-medium text-gray-900">{{ draftInvoice.useCfdi ?? '—' }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Forma pago</p>
								<p class="text-sm font-medium text-gray-900">{{ draftInvoice.paymentForm ?? '—' }}</p>
							</div>
							<div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
								<p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Método pago</p>
								<p class="text-sm font-medium text-gray-900">{{ draftInvoice.paymentMethod ?? '—' }}</p>
							</div>
						</div>

						<!-- Fecha de emisión -->
						<div class="rounded-lg bg-gray-50 border border-gray-100 p-3 flex items-center gap-2">
							<span class="material-symbols-outlined text-[14px] text-gray-400">calendar_today</span>
							<span class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Fecha de emisión</span>
							<span class="text-sm font-medium text-gray-900 ml-auto">
								{{ draftInvoice.issuedAt ? moment(draftInvoice.issuedAt).format('DD/MM/YYYY') : '—' }}
							</span>
						</div>

						<!-- Error de timbrado -->
						<div
							v-if="stampError"
							class="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3"
						>
							<span class="material-symbols-outlined text-[16px] text-red-500 mt-0.5 flex-shrink-0">error</span>
							<div>
								<p class="text-xs font-semibold text-red-800 mb-0.5">No se pudo timbrar</p>
								<p class="text-xs text-red-700 leading-relaxed">{{ stampError }}</p>
							</div>
						</div>

						<!-- PDF preview -->
						<div class="rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style="height: 320px">
							<iframe
								v-if="previewPdfEmbedUrl"
								:src="previewPdfEmbedUrl"
								class="w-full h-full"
								type="application/pdf"
							/>
							<div v-else class="flex flex-col items-center justify-center h-full gap-2 text-sm text-gray-400">
								<svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
								Cargando previsualización...
							</div>
						</div>
					</div>

					<!-- Acciones -->
					<div class="flex justify-between items-center pt-1">
						<div class="flex gap-2">
							<!-- Descargas (solo timbrada) -->
							<template v-if="stampedInvoice">
								<button
									type="button"
									:disabled="!previewPdfUrl"
									class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
									@click="handleDownloadPdf"
								>
									<span class="material-symbols-outlined text-[14px]">picture_as_pdf</span>
									PDF
								</button>
								<button
									type="button"
									:disabled="isDownloadingXml"
									class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
									@click="handleDownloadXml"
								>
									<svg v-if="isDownloadingXml" class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
										<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
										<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
									</svg>
									<span v-else class="material-symbols-outlined text-[14px]">code</span>
									XML
								</button>
							</template>
						</div>
						<div class="flex gap-3">
							<button
								type="button"
								class="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
								@click="closeInvoiceModal"
							>
								{{ stampedInvoice ? 'Listo' : 'Cerrar' }}
							</button>
							<button
								v-if="draftInvoice && !stampedInvoice"
								type="button"
								:disabled="isActing"
								class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#021F58] text-white hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
								@click="handleStamp"
							>
								<svg v-if="isActing" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
								Timbrar factura
							</button>
						</div>
					</div>
				</div>
			</div>
		</template>
	</ModalComponent>

	<ModalComponent
		:model-value="modalCancelRequest"
		@update:model-value="actionCancelRequest"
	>
		<template #modalBody>
			<div class="flex flex-col gap-6 p-6 bg-white rounded-lg">
				<div class="text-center space-y-2">
					<div
						class="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center"
					>
						<svg
							class="w-6 h-6 text-red-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
							></path>
						</svg>
					</div>
					<h3 class="text-xl font-semibold text-gray-900">
						Confirmación de cancelación de reserva
					</h3>
				</div>

				<div class="space-y-4 text-gray-700">
					<p class="text-center">
						Al cancelar esta reserva, se enviará una notificación al solicitante
						y al viajero.
					</p>

					<p>
						Si la reserva fue prepagada, el monto cobrado será reembolsado. A
						continuación, los detalles del reembolso:
					</p>
				</div>

				<div class="border border-gray-300 rounded-md p-4 my-4 bg-gray-50">
					<div class="flex items-center gap-3 mb-3">
						<svg
							class="w-5 h-5 text-gray-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
							></path>
						</svg>
						<h4 class="text-base font-semibold text-gray-900">
							Información de Reembolso
						</h4>
					</div>
					<div class="text-sm text-gray-700 space-y-2">
						<p class="font-medium">
							El reembolso se realizará de forma automática al mismo método de
							pago:
						</p>
						<ul class="list-disc list-inside space-y-1 ml-4">
							<li>
								<strong>Crédito corporativo:</strong> Se acredita de inmediato a
								su saldo
							</li>
							<li>
								<strong>Tarjeta de crédito/débito:</strong> El tiempo de
								acreditación depende de su banco
							</li>
						</ul>
					</div>
				</div>

				<div class="space-y-4">
					<div>
						<StyledTextarea
							v-model="comment"
							label="Razón de cancelación"
							:is-required="true"
							placeholder="Indique el motivo por el cual desea cancelar esta reserva"
							class="w-full"
						/>
					</div>

					<div class="bg-red-50 border border-red-200 rounded-lg p-3">
						<p class="text-red-700 text-sm font-medium">
							Esta acción no se puede deshacer. Asegúrese de indicar el motivo
							de la cancelación antes de confirmar.
						</p>
					</div>
				</div>

				<div class="flex gap-3 justify-end pt-4 border-t border-gray-200">
					<StyledButton
						class="bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200 border border-gray-300"
						@click="actionCancelRequest()"
					>
						Cancelar
					</StyledButton>

					<StyledButton
						class="bg-red-600 text-white hover:bg-red-700 transition-colors duration-200"
						@click="cancelRequest"
						:disabled="requestCancelMutationLoading"
						:loading="requestCancelMutationLoading"
					>
						Confirmar cancelación
					</StyledButton>
				</div>
			</div>
		</template>
	</ModalComponent>

	<ModalComponent
		:model-value="actionReSendEmail"
		@update:model-value="actionReSendEmail = $event"
	>
		<template #modalBody>
			<div class="p-6 bg-white rounded-lg space-y-6">
				<div class="text-center space-y-2">
					<div
						class="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"
					>
						<svg
							class="w-6 h-6 text-blue-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
							></path>
						</svg>
					</div>
					<h2 class="text-xl font-semibold text-gray-900">
						Reenviar Confirmaciones
					</h2>
					<p class="text-gray-600">
						Selecciona a quién deseas enviar las confirmaciones
					</p>
				</div>

				<form
					@submit.prevent="handleResendNotification"
					class="space-y-6"
				>
					<div class="space-y-3">
						<div
							class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
							v-for="notification in notifications"
							:key="notification.value"
						>
							<label
								class="flex items-center gap-3 flex-1"
								v-if="notification.value !== 'supervisor' || supervisor"
								:class="[
									(
										notification.value === 'traveler' &&
										request.traveler?.email === null
									) ?
										' cursor-not-allowed'
									:	'cursor-pointer',
								]"
							>
								<input
									type="checkbox"
									v-model="notification.send"
									class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
									:disabled="
										notification.value === 'traveler' &&
										request.traveler?.email === null
									"
								/>
								<div class="flex-1">
									<span class="font-medium text-gray-900">{{
										notification.label
									}}</span>
									<div class="text-sm text-gray-600">
										<span v-if="notification.value === 'member'">{{
											member?.email
										}}</span>
										<span v-if="notification.value === 'traveler'">{{
											request.traveler?.email ??
											'El viajero no tiene correo electrónico registrado'
										}}</span>
										<span v-if="notification.value === 'supervisor'">{{
											supervisor?.email
										}}</span>
									</div>
								</div>
							</label>
						</div>
					</div>

					<div class="flex gap-3 justify-end pt-4 border-t border-gray-200">
						<button
							type="button"
							@click="actionReSendEmail = false"
							class="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
						>
							Cancelar
						</button>
						<StyledButton
							type="submit"
							:disabled="!notifications.some((n) => n.send)"
							class="bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
						>
							Reenviar confirmaciones
						</StyledButton>
					</div>
				</form>
			</div>
		</template>
	</ModalComponent>
</template>
